import type {
  ChangeEventHandler,
  FocusEventHandler,
  HTMLInputTypeAttribute,
  ReactNode,
} from "react";
import { useId, useState } from "react";
import type { RegisterOptions } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";
import type { InputBaseComponentProps, TextFieldClasses } from "@mui/material";
import { Box, TextField, FormControl } from "@mui/material";
import { getValidationRules } from "./utils/getValidationRules";
import { HelperMessage } from "./HelperMessage";
import { FormControlLabel } from "./FormControlLabel";
import { useValidationIcon } from "./utils/useValidationIcon";
import { PasswordVisibilityButton } from "./PasswordVisibilityButton";
import { useTranslations } from "next-intl";

interface FormInputComponentProps {
  label: React.HTMLAttributes<HTMLLabelElement>;
}

export interface FormInputProps {
  id?: string;
  className?: string;
  disabled?: boolean;
  formatter?: (value: string) => string;
  helperText?: ReactNode;
  label?: string | ReactNode;
  name: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  prefixIcon?: React.ReactElement;
  required?: boolean;
  rules?: RegisterOptions;
  successMessage?: string;
  type?: HTMLInputTypeAttribute;
  shouldUnregister?: boolean;
  showError?: boolean;
  showValidationIcon?: boolean;
  componentProps?: FormInputComponentProps;
  classes?: Partial<TextFieldClasses>;
  inputProps?: InputBaseComponentProps;
}

const getInputRules = (
  type: string,
  t?: (key: string) => string
): RegisterOptions => {
  if (type === "email") {
    return {
      pattern: {
        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        message: t ? t("forms.validation.email") : "Please input a valid email",
      },
    };
  }

  if (type === "tel") {
    return {
      pattern: {
        value: /^\d{10}$|^\(\d{3}\)-\d{3}-\d{4}$/,
        message: t
          ? t("forms.validation.phone")
          : "Please input a valid phone number",
      },
    };
  }

  return {};
};

export const FormInput = ({
  id,
  className,
  disabled,
  formatter = (str) => str,
  helperText,
  label = "",
  name,
  onBlur,
  onFocus,
  onChange,
  placeholder,
  prefixIcon,
  required = true,
  rules = {},
  successMessage = "",
  type = "text",
  shouldUnregister = false,
  showError,
  showValidationIcon = true,
  componentProps,
  classes,
  inputProps,
  ...props
}: FormInputProps) => {
  const defaultId = useId();
  const t = useTranslations();

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const inputType = isPasswordVisible && type === "password" ? "text" : type;
  const inputRules = getInputRules(type, t);

  const validationRules = getValidationRules({
    required,
    rules: { ...inputRules, ...rules } as RegisterOptions,
    t,
  });

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const controller = useController({
    name,
    rules: validationRules,
    control,
    shouldUnregister,
  });

  const {
    field: { onChange: onFieldChange, onBlur: onFieldBlur, value },
    fieldState: { error },
  } = controller;

  const validationIcon = useValidationIcon({
    controller,
    disabled:
      (!required && Object.keys(rules).length === 0) || !showValidationIcon,
  });

  const shouldShowSuccessMessage = Boolean(successMessage) && validationIcon;

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    onFieldBlur();
    onBlur?.(e);
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const formattedValue = formatter(e.target.value);
    e.target.value = formattedValue;
    onFieldChange(e);
    onChange?.(e);
  };

  const endIcon =
    validationIcon || type === "password" ? (
      <Box className="flex items-center gap-1" component="span">
        {type === "password" && (
          <PasswordVisibilityButton
            isPasswordVisible={isPasswordVisible}
            setIsPasswordVisible={setIsPasswordVisible}
          />
        )}
        {validationIcon}
      </Box>
    ) : null;

  return (
    <FormControl className={className ?? "flex flex-1 flex-col"} {...props}>
      <FormControlLabel
        id={id ?? defaultId}
        label={label}
        labelProps={componentProps?.label}
      />
      <TextField
        InputLabelProps={{ shrink: true }}
        InputProps={{
          startAdornment: prefixIcon,
          endAdornment: endIcon,
          inputProps,
        }}
        classes={classes}
        disabled={disabled}
        error={Boolean(error)}
        helperText={helperText}
        id={id ?? defaultId}
        name={name}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={onFocus}
        placeholder={placeholder}
        size="small"
        type={inputType}
        value={value ?? ""}
        variant="outlined"
      />
      <HelperMessage
        errors={errors}
        message={successMessage}
        messageProps={{
          className: "body-m text-rmh-green-700",
          hidden: !shouldShowSuccessMessage,
        }}
        name={name}
        showError={showError}
      />
    </FormControl>
  );
};
